import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Box,
  Divider,
} from '@mui/material';
import { Close as CloseIcon, DragIndicator } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { updateModifierGroup } from '../../redux/actions/posMenu';
import { getTranslationValue } from '../../constants/posMenu';

const SortableOption = ({ option, menuMap }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.itemId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  const item = option.itemData;

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 1,
        backgroundColor: isDragging ? '#e3f2fd' : '#f5f5f5',
        borderRadius: 1,
        border: '1px solid #e0e0e0',
        '&:hover': {
          backgroundColor: '#eeeeee',
        },
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
      {...attributes}
      {...listeners}
    >
      <ListItemIcon>
        <DragIndicator sx={{ color: '#9E9EAD' }} />
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {getTranslationValue(item?.name)}
          </Typography>
        }
        secondary={
          <Typography variant="body2" color="text.secondary">
            ${(Number(item?.price) || 0).toFixed(2)}
          </Typography>
        }
      />
    </ListItem>
  );
};

const OptionSortModal = ({ open, onClose, modifierGroup, menuMap }) => {
  const dispatch = useDispatch();
  const [sortedOptions, setSortedOptions] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Initialize sorted options only when the modal opens, so menu updates made
  // while it's open don't throw away the user's drag order
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (justOpened && modifierGroup?.optionIds) {
      const options = (modifierGroup.optionIds || [])
        .map((itemId) => {
          const itemData = menuMap?.items?.[itemId];
          if (!itemData) return null;
          return {
            itemId,
            itemData,
          };
        })
        .filter(Boolean);
      setSortedOptions(options);
      setHasChanges(false);
    }
  }, [open, modifierGroup?.optionIds, menuMap]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setSortedOptions((items) => {
        const oldIndex = items.findIndex((item) => item.itemId === active.id);
        const newIndex = items.findIndex((item) => item.itemId === over.id);

        const newOrder = arrayMove(items, oldIndex, newIndex);
        setHasChanges(true);
        return newOrder;
      });
    }
  }, []);

  const handleSave = async () => {
    if (hasChanges && modifierGroup) {
      setIsSaving(true);
      const optionIds = sortedOptions.map((opt) => opt.itemId);
      try {
        await dispatch(
          updateModifierGroup(modifierGroup._id, {
            optionIds,
          })
        );
        toast.success('Option order saved');
        setHasChanges(false);
        onClose();
      } catch (error) {
        toast.error(error.message || 'Failed to save order');
      } finally {
        setIsSaving(false);
      }
    } else {
      onClose();
    }
  };

  const handleCancel = () => {
    if (modifierGroup?.optionIds) {
      const options = (modifierGroup.optionIds || [])
        .map((itemId) => {
          const itemData = menuMap?.items?.[itemId];
          if (!itemData) return null;
          return {
            itemId,
            itemData,
          };
        })
        .filter(Boolean);
      setSortedOptions(options);
    }
    setHasChanges(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '400px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" component="div">
            Sort Options
          </Typography>
          <Typography variant="body2" color="text.secondary">
            in {getTranslationValue(modifierGroup?.name)}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2, overflowX: 'hidden' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Drag and drop options to reorder them. Changes will be saved when you
          click &quot;Save&quot;.
        </Typography>

        {sortedOptions.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          >
            <SortableContext
              items={sortedOptions.map((opt) => opt.itemId)}
              strategy={verticalListSortingStrategy}
            >
              <List sx={{ padding: 0, overflowX: 'hidden' }}>
                {sortedOptions.map((option) => (
                  <SortableOption
                    key={option.itemId}
                    option={option}
                    menuMap={menuMap}
                  />
                ))}
              </List>
            </SortableContext>
          </DndContext>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
            }}
          >
            <Typography variant="body1" color="text.secondary">
              No options to sort
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleCancel} variant="outlined" sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!hasChanges || isSaving}
          sx={{
            backgroundColor: hasChanges ? '#1976d2' : undefined,
          }}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OptionSortModal;
